import { Provider } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, of } from 'rxjs';
import {
  BREADCRUMB_LABEL_RESOLVERS,
  BREADCRUMB_SIBLINGS_LOADERS,
  type BreadcrumbLabelResolver,
  type BreadcrumbResolvedItem,
  type BreadcrumbSiblingsLoader,
  type BreadcrumbItem,
} from '@zag/ui';
import type { TenantStatusResponse } from '@zag/control/models';

/**
 * Factory for creating label resolvers for the control UI.
 * Resolves dynamic labels (e.g., tenant subdomain from route param).
 */
function createLabelResolvers(): Record<string, BreadcrumbLabelResolver> {
  return {
    /**
     * Tenant resolver: simply returns the subdomain param as the label.
     * No API call needed since the subdomain IS the display name.
     */
    tenant: (params: Record<string, string>): BreadcrumbResolvedItem => {
      return { label: params['subdomain'] || 'Unknown', icon: 'dns' };
    },
  };
}

/**
 * Factory for creating siblings loaders for the control UI.
 * Loads sibling items for dropdown navigation in breadcrumbs.
 */
function createSiblingsLoaders(http: HttpClient): Record<string, BreadcrumbSiblingsLoader> {
  return {
    /**
     * Tenants siblings loader: fetches tenant list for the current environment
     * and maps them to breadcrumb items excluding the current tenant.
     *
     * The environment slug is extracted from the current URL pattern:
     *   /environments/:slug/tenants/:subdomain
     */
    tenants: (_params: Record<string, string>, currentUrl: string) => {
      // Extract environment slug from the URL: /environments/:slug/tenants/...
      const match = /\/environments\/([^/]+)\/tenants/.exec(currentUrl);
      const envSlug = match?.[1];

      if (!envSlug) {
        return of([]);
      }

      return http.get<TenantStatusResponse[]>(`/api/v1/environments/${envSlug}/tenants`).pipe(
        map(tenants =>
          tenants.slice(0, 10).map(
            (t): BreadcrumbItem => ({
              label: t.subdomain,
              route: ['/environments', envSlug, 'tenants', t.subdomain],
              icon: 'dns',
              ...(currentUrl.endsWith(`/${t.subdomain}`) ? { isCurrent: true } : {}),
            })
          )
        )
      );
    },
  };
}

/**
 * Provides breadcrumb resolvers and loaders for the control UI.
 * Add this to the control-ui app.config.ts providers array.
 */
export function provideControlBreadcrumbConfig(): Provider[] {
  return [
    {
      provide: BREADCRUMB_LABEL_RESOLVERS,
      useFactory: createLabelResolvers,
    },
    {
      provide: BREADCRUMB_SIBLINGS_LOADERS,
      useFactory: createSiblingsLoaders,
      deps: [HttpClient],
    },
  ];
}
